/*
 * Uchi PDP reveal.
 *
 * Progressive fade-in for the product-page sections. Elements are
 * visible by default — the hidden state is applied by THIS SCRIPT
 * after load. If the script never runs (JS disabled, module error,
 * strict in-app browser), nothing is hidden and content is fully
 * visible. This file must never reference or recreate the older
 * uchi-reveal.js (which was designed as a site-wide reveal and was
 * never shipped).
 *
 * Behavior:
 *   - Opts in via [data-product-reveal] on an element.
 *   - Stagger children of a [data-product-reveal-group] by 80ms per
 *     item, either by explicit data-reveal-index or by DOM order.
 *   - Fades opacity 0 → 1 and translateY(24px) → 0, 700ms ease-out,
 *     once per element.
 *   - prefers-reduced-motion: reduce → do nothing. Content stays
 *     visible. No hidden state applied.
 *   - Failsafe: 3s after init, reveal everything still hidden
 *     regardless of observer state. In-app browsers where the
 *     IntersectionObserver callback stalls will still see full
 *     content within the failsafe window.
 *   - safeInit try/catch: if anything throws, strip the hidden state
 *     from every element that has it so no user is left staring at
 *     invisible content.
 *   - pageshow (persisted): reveal all remaining hidden elements
 *     immediately when the page is restored from the bfcache.
 *
 * Not animated (never carry data-product-reveal):
 *   Gallery slide 1, the buy box, the sticky ATC bar. These are
 *   opted out in the section templates, not here.
 */
(function () {
  'use strict';

  var HIDDEN_CLASS  = 'product-reveal-hidden';
  var VISIBLE_CLASS = 'product-reveal-visible';
  var STYLE_MARKER  = 'data-product-reveal-style';
  var STAGGER_MS    = 80;
  var DURATION_MS   = 700;
  var FAILSAFE_MS   = 3000;
  var TRANSLATE_PX  = 24;

  // Reduced motion — do nothing. Content is already visible; skip the
  // whole system so users get no transitions at all.
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  // Feature detection — if IntersectionObserver or rAF are missing,
  // bail without ever hiding anything. Fail visible.
  if (!('IntersectionObserver' in window) || !window.requestAnimationFrame) {
    return;
  }

  function injectStyle() {
    if (document.head.querySelector('style[' + STYLE_MARKER + ']')) return;
    var css =
      '[data-product-reveal].' + HIDDEN_CLASS + '{' +
      'opacity:0;' +
      'transform:translateY(' + TRANSLATE_PX + 'px);' +
      'transition:' +
        'opacity '   + DURATION_MS + 'ms ease-out var(--product-reveal-delay,0ms),' +
        'transform ' + DURATION_MS + 'ms ease-out var(--product-reveal-delay,0ms);' +
      'will-change:opacity,transform;' +
      '}' +
      '[data-product-reveal].' + VISIBLE_CLASS + '{' +
      'opacity:1;' +
      'transform:none;' +
      '}';
    var style = document.createElement('style');
    style.setAttribute(STYLE_MARKER, '');
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  function init() {
    var elements = document.querySelectorAll('[data-product-reveal]');
    if (!elements.length) return;

    injectStyle();

    var pending = [];
    var observer = null;
    var failsafeTimer = null;

    function reveal(el) {
      if (!el.classList.contains(HIDDEN_CLASS)) return;
      el.classList.remove(HIDDEN_CLASS);
      el.classList.add(VISIBLE_CLASS);
      var idx = pending.indexOf(el);
      if (idx !== -1) pending.splice(idx, 1);
      if (observer) observer.unobserve(el);
      if (!pending.length && failsafeTimer) {
        window.clearTimeout(failsafeTimer);
        failsafeTimer = null;
      }
    }

    // Compute stagger for each element based on its position within a
    // [data-product-reveal-group]. Explicit data-reveal-index wins;
    // otherwise DOM order among sibling reveal targets in the group.
    elements.forEach(function (el) {
      var group = el.closest('[data-product-reveal-group]');
      var index = 0;
      if (group) {
        if (el.hasAttribute('data-reveal-index')) {
          index = parseInt(el.getAttribute('data-reveal-index'), 10);
          if (isNaN(index) || index < 0) index = 0;
        } else {
          var siblings = group.querySelectorAll('[data-product-reveal]');
          for (var i = 0; i < siblings.length; i++) {
            if (siblings[i] === el) { index = i; break; }
          }
        }
      }
      el.style.setProperty('--product-reveal-delay', (index * STAGGER_MS) + 'ms');
      el.classList.add(HIDDEN_CLASS);
      pending.push(el);
    });

    try {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            reveal(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
      elements.forEach(function (el) { observer.observe(el); });
    } catch (e) {
      // Observer construction failed — reveal everything now.
      pending.slice().forEach(reveal);
      return;
    }

    // Failsafe. If the observer never fires (in-app browser quirks, tab
    // starts in background, JS event loop starved by external scripts),
    // reveal everything after FAILSAFE_MS regardless.
    failsafeTimer = window.setTimeout(function () {
      pending.slice().forEach(reveal);
      if (observer) observer.disconnect();
    }, FAILSAFE_MS);

    // Bfcache restore: reveal everything so the animation does not
    // replay when the user hits Back.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        pending.slice().forEach(reveal);
        if (observer) observer.disconnect();
        if (failsafeTimer) {
          window.clearTimeout(failsafeTimer);
          failsafeTimer = null;
        }
      }
    });
  }

  function safeInit() {
    try {
      init();
    } catch (e) {
      // Something threw. Strip the hidden state from any element that
      // received it so nothing is permanently invisible.
      var stuck = document.querySelectorAll('.' + HIDDEN_CLASS);
      for (var i = 0; i < stuck.length; i++) {
        stuck[i].classList.remove(HIDDEN_CLASS);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeInit);
  } else {
    safeInit();
  }
})();
